// REQ-081 — Tab "Heatmap": matriz OU × Fundamental pintada con DOM (sin librería de red). El color
// de cada celda sale del Score 0-1 (madurez). Celdas `suprimido` se muestran como "—". SIN puntos.
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecognitionHeatmap, useRecognitionCatalog, getFundamentalColor } from '@/api/recognition';
import type { RecognitionReportParams } from '@/api/recognition';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { AvisoFiltroNoAplica } from '../AvisoFiltroNoAplica';
import {
  ReportStatus, fundamentalTintStyle, INTENSIDAD_MINIMA_SIN_DATO, filtrosActivosDe, soloPersonaDe,
} from '../reportsShared';
import {
  buildHeatmapMatrix, sumaFilaVisible, sumaColumnaVisible, maximoDeColumna, celdasFiltradasPorFundamental,
} from './heatmapMatrix';

export function HeatmapTab({ params }: Readonly<{ params: RecognitionReportParams }>) {
  const { t } = useTranslation('recognition');
  const q = useRecognitionHeatmap(params);
  const cells = useMemo(() => q.data ?? [], [q.data]);
  // Recorte por Fundamental — compartido con el export consolidado (`DemograficoExportButton`, ver
  // el comentario de `celdasFiltradasPorFundamental`), para que pantalla y Excel nunca diverjan.
  const celdasVisibles = useMemo(
    () => celdasFiltradasPorFundamental(cells, params.fundamentalId),
    [cells, params.fundamentalId],
  );
  const isEmpty = !!q.data && celdasVisibles.length === 0;
  const { resolveOuName } = useOuNameResolver();
  // La persona no acota esta matriz (agrupa por OU); solo el Fundamental lo hace, y ya arriba.
  const soloPersona = useMemo(() => soloPersonaDe(filtrosActivosDe(params)), [params]);

  // Color de cada columna: el mismo del catálogo de Fundamentales que usa el resto de la
  // Reportería, no una paleta inventada para este mapa.
  const { data: catalog } = useRecognitionCatalog();
  const colorPorFundamental = useMemo(() => {
    const porId = new Map<string, string>();
    (catalog ?? []).forEach((f, indice) => porId.set(f.id, getFundamentalColor(f, indice)));
    return porId;
  }, [catalog]);

  // Orden de columnas = orden del catálogo (`FundamentalDto.order`), el mismo criterio que
  // «Brecha Generacional × Fundamental» — no el de primera aparición en `cells` (dependiente del
  // orden de iteración de un `Dictionary` en el backend).
  const fundamentalOrder = useMemo(
    () => new Map((catalog ?? []).map((f) => [f.id, f.order])),
    [catalog],
  );

  // Ejes (OUs = filas, Fundamentales = columnas, en orden de catálogo) derivados de las celdas.
  // C7 — pivote compartido con el export consolidado del panel Demográfico (`heatmapMatrix.ts`).
  const { ous, funds, lookup } = useMemo(
    () => buildHeatmapMatrix(celdasVisibles, resolveOuName, fundamentalOrder),
    [celdasVisibles, resolveOuName, fundamentalOrder],
  );

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-table-cells" aria-hidden="true" /> {t('reports.heatmap.title')}
          </div>
          <div className="au-card-sub">{t('reports.heatmap.subtitle')}</div>
        </div>
      </div>

      <AvisoFiltroNoAplica filtros={soloPersona} motivo="porOu" />

      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        onRetry={() => q.refetch()}
      >
        <div className="rr-hm-wrap">
          <table className="rr-hm-table">
            <thead>
              <tr>
                <th className="rr-hm-th-ou">{t('reports.heatmap.ou')}</th>
                {funds.map((f) => (
                  <th key={f.id} style={{ color: colorPorFundamental.get(f.id) }}>{f.name}</th>
                ))}
                {/* C2 — la maqueta cierra la fila con «Total» y aquí faltaba. Sin ella, comparar dos
                    OU obliga a sumar cinco celdas a ojo, que es justo lo que un mapa de calor debería
                    ahorrar. */}
                <th className="rr-num">{t('reports.heatmap.total')}</th>
              </tr>
            </thead>
            <tbody>
              {ous.map((ou) => (
                <tr key={ou.id}>
                  <td className="rr-hm-ou">{ou.name}</td>
                  {funds.map((f) => {
                    const cell = lookup.get(`${ou.id}::${f.id}`);
                    if (!cell || cell.suprimido) {
                      // Sin dato (o tapado por anonimato): mismo tinte de columna que una celda con
                      // valor, pero casi invisible — se nota de qué Fundamental es la columna aunque
                      // no haya nada que colorear con fuerza.
                      return (
                        <td key={f.id}>
                          <div
                            className="rr-hm-cell rr-hm-empty"
                            style={fundamentalTintStyle(
                              colorPorFundamental.get(f.id) ?? '#6b7280', 0, INTENSIDAD_MINIMA_SIN_DATO,
                            )}
                          >
                            —
                          </div>
                        </td>
                      );
                    }
                    // Intensidad relativa DENTRO de la columna, no un score absoluto: la misma
                    // cantidad de reconocimientos se ve más intensa en una columna con volumen bajo
                    // que en una con volumen alto — es la concentración de ESE Fundamental en ESA OU.
                    const ratio = cell.count / maximoDeColumna(f.id, ous, lookup);
                    return (
                      <td key={f.id}>
                        <div
                          className="rr-hm-cell"
                          style={fundamentalTintStyle(colorPorFundamental.get(f.id) ?? '#6b7280', ratio)}
                          title={`${ou.name} · ${f.name} — ${cell.count}`}
                        >
                          {cell.count}
                        </div>
                      </td>
                    );
                  })}
                  {/* Suma de las celdas VISIBLES de la fila. Las celdas suprimidas por anonimato (si
                      el umbral vuelve a activarse) no se suman —no se conoce su valor—, pero el
                      total se muestra siempre como número plano: la referencia de diseño no
                      distingue un total parcial de uno completo. */}
                  {(() => {
                    const { suma } = sumaFilaVisible(ou.id, funds, lookup);
                    return <td className="rr-num">{suma}</td>;
                  })()}
                </tr>
              ))}
              {/* Fila «Total» al pie: suma por columna, mismo criterio de número plano que la
                  columna de la derecha — las celdas ocultas por anonimato siguen sin sumarse
                  (`sumaColumnaVisible`/`sumaFilaVisible`), solo deja de anunciarse con el «≥». */}
              <tr className="rr-hm-total-row">
                <th className="rr-hm-th-ou">{t('reports.heatmap.total')}</th>
                {funds.map((f) => {
                  const { suma } = sumaColumnaVisible(f.id, ous, lookup);
                  return (
                    <td key={f.id} className="rr-num" style={{ color: colorPorFundamental.get(f.id) }}>
                      {suma}
                    </td>
                  );
                })}
                <td className="rr-num">
                  {ous.reduce((acc, ou) => acc + sumaFilaVisible(ou.id, funds, lookup).suma, 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ReportStatus>
    </div>
  );
}
